import { useState, useCallback, useEffect, useRef } from 'react'

export interface DevicePreviewState {
  videoRef: React.RefObject<HTMLVideoElement | null>
  cameras: MediaDeviceInfo[]
  mics: MediaDeviceInfo[]
  selectedVideoId: string
  selectedAudioId: string
  micMuted: boolean
  permError: string
  setSelectedVideoId: (id: string) => void
  setSelectedAudioId: (id: string) => void
  toggleMic: () => void
  stopPreview: () => void
}

export function useDevicePreview(): DevicePreviewState {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([])
  const [mics, setMics] = useState<MediaDeviceInfo[]>([])
  const [selectedVideoId, setSelectedVideoId] = useState('')
  const [selectedAudioId, setSelectedAudioId] = useState('')
  const [micMuted, setMicMuted] = useState(false)
  const [permError, setPermError] = useState('')

  const toggleMic = useCallback(() => setMicMuted(m => !m), [])

  const stopPreview = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop())
      streamRef.current = null
    }
  }, [])

  // Enumerate devices after permission grant
  const refreshDevices = useCallback(async () => {
    try {
      const all = await navigator.mediaDevices.enumerateDevices()
      setCameras(all.filter(d => d.kind === 'videoinput'))
      setMics(all.filter(d => d.kind === 'audioinput'))
    } catch (e) {
      console.warn('[useDevicePreview] enumerateDevices:', e)
    }
  }, [])

  // Start/restart preview stream when device selection changes
  useEffect(() => {
    let cancelled = false
    const start = async () => {
      // Stop previous stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop())
        streamRef.current = null
      }
      try {
        const constraints: MediaStreamConstraints = {
          video: selectedVideoId ? { deviceId: { exact: selectedVideoId } } : true,
          audio: selectedAudioId ? { deviceId: { exact: selectedAudioId } } : true,
        }
        const stream = await navigator.mediaDevices.getUserMedia(constraints)
        if (cancelled) {
          stream.getTracks().forEach(t => t.stop())
          return
        }
        streamRef.current = stream
        setPermError('')
        if (videoRef.current) {
          videoRef.current.srcObject = stream
        }
        // Enumerate after permission grant (labels become available)
        await refreshDevices()
        // Auto-select first device if none selected
        if (!selectedVideoId) {
          const vId = stream.getVideoTracks()[0]?.getSettings()?.deviceId
          if (vId) setSelectedVideoId(vId)
        }
        if (!selectedAudioId) {
          const aId = stream.getAudioTracks()[0]?.getSettings()?.deviceId
          if (aId) setSelectedAudioId(aId)
        }
      } catch (e) {
        if (!cancelled) {
          console.warn('[useDevicePreview] getUserMedia:', e)
          setPermError('Camera/micro non accessible. Verifiez les permissions du navigateur.')
        }
      }
    }
    start()
    return () => {
      cancelled = true
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop())
        streamRef.current = null
      }
    }
  }, [selectedVideoId, selectedAudioId, refreshDevices])

  // Apply mic mute to audio track
  useEffect(() => {
    if (!streamRef.current) return
    for (const t of streamRef.current.getAudioTracks()) {
      t.enabled = !micMuted
    }
  }, [micMuted])

  return {
    videoRef,
    cameras,
    mics,
    selectedVideoId,
    selectedAudioId,
    micMuted,
    permError,
    setSelectedVideoId,
    setSelectedAudioId,
    toggleMic,
    stopPreview,
  }
}
